import { DayTimeWindow, KeyValueRow, newRow, newWindow } from '../configStudio/configModel';
import {
  LimitPeriod,
  newTrigger,
  PickedItem,
  TriggerKind,
  WizardTrigger
} from '../wizard/wizardModel';

/** The six trigger tiles of 46, in drawn order; `customer` is the module's customer trigger. */
export type RuleTriggerKind = TriggerKind | 'customer';

export const RULE_TRIGGER_KINDS: RuleTriggerKind[] = [
  'transaction',
  'loyalty',
  'milestone',
  'reward',
  'customer',
  'others'
];

/** The module's customer events (`EventNameField`), without the undrawn `user_reactivated`. */
export type CustomerEvent =
  | 'customer_signed_up'
  | 'customer_signed_in'
  | 'customer_profile_updated';

export const CUSTOMER_EVENTS: CustomerEvent[] = [
  'customer_signed_up',
  'customer_signed_in',
  'customer_profile_updated'
];

/** The seven outcome tiles of 45, in drawn order. */
export type RuleOutcomeKind =
  | 'stamp'
  | 'game'
  | 'reward'
  | 'points'
  | 'audience'
  | 'customer'
  | 'push';

export const RULE_OUTCOME_KINDS: RuleOutcomeKind[] = [
  'stamp',
  'game',
  'reward',
  'points',
  'audience',
  'customer',
  'push'
];

export type RuleStep =
  | 'trigger'
  | 'triggerDetails'
  | 'outcome'
  | 'outcomeDetails'
  | 'schedule'
  | 'review';

export const RULE_STEP_TOTAL = 4;

export const RULE_STEP_NUMBER: Record<RuleStep, number | null> = {
  trigger: 1,
  triggerDetails: 2,
  outcome: 3,
  outcomeDetails: 3,
  schedule: 4,
  review: null
};

const ORDER: RuleStep[] = [
  'trigger',
  'triggerDetails',
  'outcome',
  'outcomeDetails',
  'schedule',
  'review'
];

export interface RuleOutcome {
  kind: RuleOutcomeKind | null;
  campaign: PickedItem | null;
  quantity: string;
  reward: PickedItem | null;
  loyalty: PickedItem | null;
  points: string;
  audience: PickedItem | null;
  properties: KeyValueRow[];
  mediaTag: number | null;
  payload: KeyValueRow[];
}

export interface RuleLimits {
  totalOn: boolean;
  total: string;
  perPersonOn: boolean;
  perPerson: string;
  rateOn: boolean;
  rate: string;
  ratePeriod: LimitPeriod;
  userRateOn: boolean;
  userRate: string;
  userRatePeriod: LimitPeriod;
}

export interface RuleWizardState {
  step: RuleStep;
  triggerKind: RuleTriggerKind | null;
  /** The transaction, points, campaign, reward and custom answers; the campaign wizard's shape. */
  trigger: WizardTrigger;
  customerEvent: CustomerEvent;
  /** 47 "Only count transactions from these merchants": drawn, no module key (backend ask). */
  merchantsOn: boolean;
  firstOnly: boolean;
  outcome: RuleOutcome;
  beginsAt: string | null;
  endsOn: boolean;
  endsAt: string | null;
  window: DayTimeWindow;
  limits: RuleLimits;
  name: string;
}

export const newOutcome = (kind: RuleOutcomeKind | null = null): RuleOutcome => ({
  kind,
  campaign: null,
  quantity: '',
  reward: null,
  loyalty: null,
  points: '',
  audience: null,
  properties: [newRow()],
  mediaTag: null,
  payload: [newRow()]
});

export const initialRuleState = (): RuleWizardState => ({
  step: 'trigger',
  triggerKind: null,
  trigger: newTrigger('transaction'),
  customerEvent: 'customer_signed_up',
  merchantsOn: false,
  firstOnly: false,
  outcome: newOutcome(),
  beginsAt: null,
  endsOn: false,
  endsAt: null,
  window: newWindow(),
  limits: {
    totalOn: false,
    total: '',
    perPersonOn: false,
    perPerson: '',
    rateOn: false,
    rate: '',
    ratePeriod: 'day',
    userRateOn: false,
    userRate: '',
    userRatePeriod: 'day'
  },
  name: ''
});

/** A tile pick starts that trigger's answers over and moves on (46 draws no Next). */
export const chooseTrigger = (state: RuleWizardState, kind: RuleTriggerKind): RuleWizardState => ({
  ...state,
  triggerKind: kind,
  trigger:
    kind === state.triggerKind ? state.trigger : newTrigger(kind === 'customer' ? 'others' : kind),
  step: 'triggerDetails'
});

export const chooseOutcome = (state: RuleWizardState, kind: RuleOutcomeKind): RuleWizardState => ({
  ...state,
  outcome: kind === state.outcome.kind ? state.outcome : newOutcome(kind),
  step: 'outcomeDetails'
});

export const nextRuleStep = (step: RuleStep): RuleStep =>
  ORDER[Math.min(ORDER.indexOf(step) + 1, ORDER.length - 1)];

export const previousRuleStep = (step: RuleStep): RuleStep =>
  ORDER[Math.max(ORDER.indexOf(step) - 1, 0)];

/** The bar's earlier segments jump back to the first screen of that step number. */
export const ruleStepAt = (number: number): RuleStep =>
  ORDER.find(step => RULE_STEP_NUMBER[step] === number) ?? 'trigger';

export interface IRuleStepProps {
  state: RuleWizardState;
  update: (change: (current: RuleWizardState) => RuleWizardState) => void;
  next: () => void;
  back: () => void;
}
